import type { ILead } from 'src/services/api';
import type { IShowing } from 'src/api/landlord-showings-api';
import { getTimezoneAbbr, parseDateTimeInUserTimezone } from 'src/utils/format-date-time';
import { getDisplayedFacts } from 'src/components/lead-sidebar/displayed-lead-facts';

type Params = {
    lead: ILead;
    /** Display name for the lead, e.g. from LeadUtils.getLeadName */
    leadName: string;
    /**
     * If this lead is associated with a specific property
     */
    propertyName?: string;
    upcomingTour?: IShowing;
    timezoneOverride?: string | null;
};

/**
 * Format a tour's start time as `ddd, MMM D, YYYY at h:mm A TZ`,
 * e.g. `Mon, Jul 13, 2026 at 11:00 AM PDT`.
 */
const formatTourDateTime = (tour: IShowing, timezoneOverride?: string | null): string => {
    const tourDate = parseDateTimeInUserTimezone(tour.start_time, timezoneOverride);
    const timezone = timezoneOverride ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    const tzAbbr = timezone ? ` ${getTimezoneAbbr(timezone)}` : '';
    return `${tourDate.format('ddd, MMM D, YYYY')} at ${tourDate.format('h:mm A')}${tzAbbr}`;
};

/**
 * Format lead details as plain text for sharing (e.g. copying to clipboard).
 * Sections with no data are omitted.
 */
export const formatLeadShareText = ({
    lead,
    leadName,
    propertyName,
    upcomingTour,
    timezoneOverride,
}: Params): string => {
    const sections: string[] = [`Lead Name: ${leadName}`];

    const contactLines: string[] = [];
    if (lead.email) {
        contactLines.push(`* email: ${lead.email}`);
    }
    if (lead.phone_number) {
        contactLines.push(`* phone: ${lead.phone_number}`);
    }
    if (contactLines.length > 0) {
        sections.push(['Contact Information', ...contactLines].join('\n'));
    }

    if (propertyName) {
        sections.push(`Property Interest\n${propertyName}`);
    }

    const factLines = getDisplayedFacts(lead).map(({ label, value }) => `* ${label}: ${value}`);
    if (factLines.length > 0) {
        sections.push(['Lead Info', ...factLines].join('\n'));
    }

    const notes = lead.notes?.contents?.trim();
    if (notes) {
        sections.push(`Notes\n${notes}`);
    }

    if (upcomingTour) {
        sections.push(`Tour Info\nTime: ${formatTourDateTime(upcomingTour, timezoneOverride)}`);
    }

    return sections.join('\n\n');
};

type OnsiteTeamRecipient = {
    email: string;
    name?: string;
};

/**
 * Parse an `onsite_team` property fact value into recipients.
 * Entries are separated by `&`, each `email (name)` or a bare `email`, e.g.
 * `a@x.com (Al) & b@y.com (Bo)`, `a@x.com (Al)`, `a@x.com & b@y.com`.
 */
export const parseOnsiteTeam = (value: string): OnsiteTeamRecipient[] =>
    value
        .split('&')
        .map((entry) => entry.trim())
        .filter(Boolean)
        .map((entry) => {
            const match = entry.match(/^(.*?)\s*\((.*)\)\s*$/);
            if (match) {
                return { email: match[1].trim(), name: match[2].trim() };
            }
            return { email: entry };
        });

type MailtoParams = Params & {
    /** Value of the property's `onsite_team` fact. Caller must ensure it is defined. */
    onsiteTeam: string;
};

/**
 * Build a `mailto:` deep link that opens the user's mail client (e.g. Gmail)
 * pre-addressed to the onsite team, with the lead share text as the body.
 */
export const getShareLeadMailtoLink = (params: MailtoParams): string => {
    const { onsiteTeam, leadName, upcomingTour, timezoneOverride } = params;

    const recipients = parseOnsiteTeam(onsiteTeam)
        .map(({ email, name }) => (name ? `${name} <${email}>` : email))
        .map(encodeURIComponent)
        .join(',');

    const tourSuffix = upcomingTour ? ` ${formatTourDateTime(upcomingTour, timezoneOverride)}` : '';
    const subject = `Tour Scheduled - ${leadName}${tourSuffix}`;
    const body = `Hi Team,\n\n${formatLeadShareText(params)}`;

    const query = `subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    return `mailto:${recipients}?${query}`;
};
